import { Edge } from '@xyflow/react';
import { PedigreeNode } from '../types';
import {
  DEFAULT_STEP_HEIGHT,
  OVERLAPPING_EDGE_STEP_INCREMENT,
  PERSON_NODE_DIMENSIONS,
} from '../visual-consts';

// -----------------------------------------------------------------------------
interface EdgePath {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  type: string;
}

// -----------------------------------------------------------------------------
const doLinesIntersect = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean => {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (denom === 0) {
    // Lines are parallel
    return false;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  return t > 0 && t < 1 && u > 0 && u < 1;
};

// -----------------------------------------------------------------------------
const areEdgesOverlapping = (edge1: EdgePath, edge2: EdgePath): boolean => {
  // Don't check edges that share a common node
  if (edge1.id.split('-').some((id) => edge2.id.includes(id))) {
    return false;
  }

  // Check for intersection
  if (
    doLinesIntersect(
      edge1.startX,
      edge1.startY,
      edge1.endX,
      edge1.endY,
      edge2.startX,
      edge2.startY,
      edge2.endX,
      edge2.endY
    )
  ) {
    return true;
  }

  // Check for parallel lines that are too close
  const threshold = 15; // pixels

  // Calculate distance between parallel lines
  const distance1to2Start = Math.sqrt(
    Math.pow(edge1.startX - edge2.startX, 2) +
      Math.pow(edge1.startY - edge2.startY, 2)
  );
  const distance1to2End = Math.sqrt(
    Math.pow(edge1.endX - edge2.endX, 2) + Math.pow(edge1.endY - edge2.endY, 2)
  );

  return distance1to2Start < threshold || distance1to2End < threshold;
};

// -----------------------------------------------------------------------------
// Detect overlapping edges and return groups with their step heights
export const detectOverlappingEdges = (
  edges: Edge[],
  nodes: PedigreeNode[]
): Map<string, { stepHeight: number; isOverlapping: boolean }> => {
  const edgeMetadata = new Map<
    string,
    { stepHeight: number; isOverlapping: boolean }
  >();
  const edgePaths: EdgePath[] = [];

  // Create a map of node positions for quick lookup
  const nodePositions = new Map<string, { x: number; y: number }>();
  nodes.forEach((node) => {
    nodePositions.set(node.id, { x: node.position.x, y: node.position.y });
  });

  // Convert edges to edge paths
  edges.forEach((edge) => {
    const sourcePos = nodePositions.get(edge.source);
    const targetPos = nodePositions.get(edge.target);

    if (sourcePos && targetPos) {
      edgePaths.push({
        id: edge.id,
        startX: sourcePos.x + PERSON_NODE_DIMENSIONS.width / 2,
        startY: sourcePos.y + PERSON_NODE_DIMENSIONS.height / 2,
        endX: targetPos.x + PERSON_NODE_DIMENSIONS.width / 2,
        endY: targetPos.y + PERSON_NODE_DIMENSIONS.height / 2,
        type: edge.type || 'default',
      });
    }
  });

  // Find all overlapping edge groups using Union-Find algorithm
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    const currentParent = parent.get(x);
    if (currentParent !== undefined && currentParent !== x) {
      const root = find(currentParent);
      parent.set(x, root);
      return root;
    }
    return x;
  };

  const union = (x: string, y: string) => {
    const rootX = find(x);
    const rootY = find(y);
    if (rootX !== rootY) {
      parent.set(rootX, rootY);
    }
  };

  // Initialize each edge as its own parent
  edgePaths.forEach((edge) => {
    parent.set(edge.id, edge.id);
  });

  // Check all pairs of edges for overlap and group them
  for (let i = 0; i < edgePaths.length; i++) {
    for (let j = i + 1; j < edgePaths.length; j++) {
      if (areEdgesOverlapping(edgePaths[i], edgePaths[j])) {
        union(edgePaths[i].id, edgePaths[j].id);
      }
    }
  }

  // Group edges by their root parent
  const groups = new Map<string, string[]>();
  edgePaths.forEach((edge) => {
    const root = find(edge.id);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    const group = groups.get(root);
    if (group) {
      group.push(edge.id);
    }
  });

  // Assign step heights to each group
  groups.forEach((edgeIds) => {
    const isOverlapping = edgeIds.length > 1;
    edgeIds.forEach((edgeId, index) => {
      const stepHeight = isOverlapping
        ? DEFAULT_STEP_HEIGHT + index * OVERLAPPING_EDGE_STEP_INCREMENT
        : DEFAULT_STEP_HEIGHT;

      edgeMetadata.set(edgeId, {
        stepHeight,
        isOverlapping,
      });
    });
  });

  return edgeMetadata;
};
